import type { ContactInquiryAccepted } from '../api/generated/models/ContactInquiryAccepted';
import type { ContactInquiryRequest } from '../api/generated/models/ContactInquiryRequest';
import type { ContactPublicReadiness } from '../api/generated/models/ContactPublicReadiness';

const GENERIC_CONTACT_ERROR =
  'The contact service could not complete this request.';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseReadiness(value: unknown): ContactPublicReadiness {
  if (!isRecord(value) || !isRecord(value.contact)) {
    throw new PublicContactError(null, true);
  }

  const { privacy_notice_version, status, turnstile_site_key } = value.contact;
  if (status === 'disabled') {
    if (
      !(
        privacy_notice_version === null ||
        privacy_notice_version === undefined
      ) ||
      !(turnstile_site_key === null || turnstile_site_key === undefined)
    ) {
      throw new PublicContactError(null, true);
    }
    return {
      privacy_notice_version: null,
      status,
      turnstile_site_key: null,
    };
  }

  if (
    status !== 'ready' ||
    typeof privacy_notice_version !== 'string' ||
    privacy_notice_version.length === 0 ||
    typeof turnstile_site_key !== 'string' ||
    turnstile_site_key.length === 0
  ) {
    throw new PublicContactError(null, true);
  }
  return { privacy_notice_version, status, turnstile_site_key };
}

function parseAccepted(value: unknown): ContactInquiryAccepted {
  if (
    !isRecord(value) ||
    (value.status !== undefined && value.status !== 'accepted') ||
    typeof value.reference !== 'string' ||
    !/^FW-[0-9A-F]{16}$/.test(value.reference)
  ) {
    throw new PublicContactError(null, true);
  }
  return { reference: value.reference, status: 'accepted' };
}

export class PublicContactError extends Error {
  readonly requiresNewIdempotencyKey: boolean;
  readonly retryable: boolean;
  readonly status: number | null;

  constructor(status: number | null, retryable: boolean) {
    super(GENERIC_CONTACT_ERROR);
    this.name = 'PublicContactError';
    this.requiresNewIdempotencyKey = status === 409;
    this.retryable = retryable;
    this.status = status;
  }
}

export interface PublicContactClient {
  loadReadiness(signal?: AbortSignal): Promise<ContactPublicReadiness>;
  submit(
    request: ContactInquiryRequest,
    signal?: AbortSignal,
  ): Promise<ContactInquiryAccepted>;
}

export function createPublicContactClient(
  fetchImplementation: typeof fetch = window.fetch.bind(window),
): PublicContactClient {
  return {
    async loadReadiness(signal) {
      let response: Response;
      try {
        response = await fetchImplementation('/api/v1/readiness', {
          cache: 'no-store',
          credentials: 'omit',
          headers: { Accept: 'application/json' },
          method: 'GET',
          redirect: 'error',
          referrerPolicy: 'same-origin',
          signal: signal ?? null,
        });
      } catch (error) {
        if (error instanceof PublicContactError) throw error;
        throw new PublicContactError(null, true);
      }
      if (!response.ok) {
        throw new PublicContactError(response.status, response.status >= 500);
      }
      try {
        return parseReadiness(await response.json());
      } catch (error) {
        if (error instanceof PublicContactError) throw error;
        throw new PublicContactError(null, true);
      }
    },

    async submit(request, signal) {
      let response: Response;
      try {
        response = await fetchImplementation('/api/v1/contact-inquiries', {
          body: JSON.stringify(request),
          cache: 'no-store',
          credentials: 'omit',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          method: 'POST',
          redirect: 'error',
          referrerPolicy: 'same-origin',
          signal: signal ?? null,
        });
      } catch (error) {
        if (error instanceof PublicContactError) throw error;
        throw new PublicContactError(null, true);
      }
      if (!response.ok) {
        throw new PublicContactError(
          response.status,
          response.status === 408 ||
            response.status === 429 ||
            response.status >= 500,
        );
      }
      try {
        return parseAccepted(await response.json());
      } catch (error) {
        if (error instanceof PublicContactError) throw error;
        throw new PublicContactError(null, true);
      }
    },
  };
}

export const publicContactClient = createPublicContactClient((input, init) =>
  window.fetch(input, init),
);
